import { faAdd, faArrowLeft, faClone, faLayerGroup, faList, faTrash } from '@fortawesome/free-solid-svg-icons'
import '../../Components/VariablesTable.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Outlet, useMatchRoute, useNavigate } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useRef, useState } from 'react'
import { CreateExpressionVariableControlId, ParseControlId } from '@companion-app/shared/ControlId.js'
import type {
	ClientExpressionVariableData,
	ExpressionVariableCollection,
} from '@companion-app/shared/Model/ExpressionVariableModel.js'
import { Button, ButtonGroup, LinkButton } from '~/Components/Button'
import { CollectionsNestingTable } from '~/Components/CollectionsNestingTable/CollectionsNestingTable'
import { CopyButton } from '~/Components/CopyButton'
import { GenericConfirmModal, type GenericConfirmModalRef } from '~/Components/GenericConfirmModal.js'
import { NonIdealState } from '~/Components/NonIdealState.js'
import { SearchBox } from '~/Components/SearchBox'
import { PanelCollapseHelperProvider } from '~/Helpers/CollapseHelper'
import { CloseButton, ContextHelpButton } from '~/Layout/PanelIcons'
import { SplitPanels } from '~/Layout/SplitPanels.js'
import { trpc, useMutationExt } from '~/Resources/TRPC'
import { useComputed } from '~/Resources/util'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { useExpressionVariablesCollectionsApi } from './ExpressionVariablesCollectionsApi'
import {
	ExpressionVariablesTableContextProvider,
	useExpressionVariablesTableContext,
} from './ExpressionVariablesTableContext'

export const ExpressionVariablesPage = observer(function ExpressionVariablesPage() {
	const { expressionVariablesList } = useContext(RootAppStoreContext)

	const navigate = useNavigate({ from: '/variables/expression' })

	const createMutation = useMutationExt(trpc.controls.expressionVariables.create.mutationOptions())

	const doAddNew = useCallback(
		(_e: React.MouseEvent<HTMLButtonElement>) => {
			createMutation
				.mutateAsync()
				.then(async (controlId) => {
					console.log('created expression variable', controlId)

					const parsedId = ParseControlId(controlId)
					if (parsedId?.type !== 'expression-variable') return

					await navigate({
						to: `/variables/expression/$controlId`,
						params: {
							controlId: parsedId.variableId,
						},
					})
				})
				.catch((e) => {
					console.error('failed to create expression-variable', e)
				})
		},
		[createMutation, navigate]
	)

	const confirmModalRef = useRef<GenericConfirmModalRef>(null)
	const expressionVariablesGroupsApi = useExpressionVariablesCollectionsApi(confirmModalRef)

	const allExpressionVariables = useComputed(() => {
		const allExpressionVariables: ExpressionVariableDataWithId[] = []

		for (const [variableId, variable] of expressionVariablesList.expressionVariables) {
			const parsedId = ParseControlId(variableId)
			if (!parsedId || parsedId.type !== 'expression-variable') continue
			allExpressionVariables.push({ ...variable, id: parsedId.variableId, collectionId: variable.collectionId || null })
		}

		return allExpressionVariables
	}, [expressionVariablesList.expressionVariables])

	const [filter, setFilter] = useState('')

	let filterRegexp: RegExp | null = null
	if (filter) {
		try {
			filterRegexp = new RegExp(filter, 'i')
		} catch (e) {
			console.error('Failed to compile filter regexp:', e)
		}
	}

	const ExpressionVariableItemRow = (item: ExpressionVariableDataWithId) => {
		if (filterRegexp && !item.variableName.match(filterRegexp)) return null

		return <ExpressionVariableTableRow item={item} />
	}

	const matchRoute = useMatchRoute()
	const routeMatch = matchRoute({ to: '/variables/expression/$controlId' })
	const selectedVariableId = routeMatch ? routeMatch.controlId : null

	const selectExpressionVariable = useCallback(
		(variableId: string | null) => {
			if (variableId === null) {
				void navigate({ to: '/variables/expression' })
			} else {
				void navigate({
					to: `/variables/expression/$controlId`,
					params: {
						controlId: variableId,
					},
				})
			}
		},
		[navigate]
	)

	const doCloseVariable = useCallback(() => {
		void navigate({ to: '/variables/expression' })
	}, [navigate])

	return (
		<SplitPanels.Root
			showing={selectedVariableId ? 'secondary' : 'primary'}
			className="triggers-page"
			resize={{ storageKey: 'expression-variables' }}
		>
			<GenericConfirmModal ref={confirmModalRef} />

			<SplitPanels.Primary>
				<h4 className="button-inline">
					Expression Variables
					<ContextHelpButton action="/user-guide/config/variables#expression-variables" />
				</h4>
				<p className="mb-2">Here you can create some variables from live computed expressions</p>

				<div className="mb-2">
					<ButtonGroup>
						<LinkButton color="primary" to="/variables" size="sm">
							<FontAwesomeIcon icon={faArrowLeft} />
							&nbsp; Go back
						</LinkButton>
						<Button color="warning" onClick={doAddNew} size="sm">
							<FontAwesomeIcon icon={faAdd} /> Add Expression Variable
						</Button>
						<CreateCollectionButton />
					</ButtonGroup>

					<SearchBox placeholder="Filter ..." filter={filter} setFilter={setFilter} className="mb-1 mt-2" />
				</div>

				<PanelCollapseHelperProvider
					storageId="expression-variable-groups"
					knownPanelIds={expressionVariablesList.allCollectionIds}
					defaultCollapsed
				>
					<ExpressionVariablesTableContextProvider
						deleteModalRef={confirmModalRef}
						selectExpressionVariable={selectExpressionVariable}
					>
						<CollectionsNestingTable<ExpressionVariableCollection, ExpressionVariableDataWithId>
							// Heading={ExpressionVariablesListTableHeading}
							NoContent={ExpressionVariablesListNoContent}
							ItemRow={ExpressionVariableItemRow}
							itemName="expression variable"
							dragId="expression-variable"
							collectionsApi={expressionVariablesGroupsApi}
							collections={expressionVariablesList.rootCollections()}
							items={allExpressionVariables}
							selectedItemId={selectedVariableId}
						/>
					</ExpressionVariablesTableContextProvider>
				</PanelCollapseHelperProvider>
			</SplitPanels.Primary>

			<SplitPanels.Secondary>
				<div className="secondary-panel-simple">
					{!!selectedVariableId && <ExpressionVariableEditPanelHeading doCloseVariable={doCloseVariable} />}
					<Outlet />
				</div>
			</SplitPanels.Secondary>
		</SplitPanels.Root>
	)
})

export interface ExpressionVariableDataWithId extends Omit<ClientExpressionVariableData, 'collectionId'> {
	id: string
	collectionId: string | null
}

function ExpressionVariablesListNoContent() {
	return <NonIdealState icon={faList} text="There are currently no expression variables." />
}

interface ExpressionVariableTableRowProps {
	item: ExpressionVariableDataWithId
}

const ExpressionVariableTableRow = observer(function ExpressionVariableTableRow2({
	item,
}: ExpressionVariableTableRowProps) {
	const tableContext = useExpressionVariablesTableContext()

	const deleteMutation = useMutationExt(trpc.controls.expressionVariables.delete.mutationOptions())
	const cloneMutation = useMutationExt(trpc.controls.expressionVariables.clone.mutationOptions())

	const doDelete = useCallback(() => {
		tableContext.deleteModalRef.current?.show(
			'Delete expression variable',
			'Are you sure you wish to delete this expression variable?',
			'Delete',
			() => {
				deleteMutation.mutateAsync({ controlId: CreateExpressionVariableControlId(item.id) }).catch((e) => {
					console.error('Failed to delete', e)
				})
			}
		)
	}, [deleteMutation, tableContext.deleteModalRef, item.id])

	const doEdit = useCallback(() => {
		tableContext.selectExpressionVariable(item.id)
	}, [tableContext, item.id])

	const doClone = useCallback(() => {
		cloneMutation
			.mutateAsync({ controlId: CreateExpressionVariableControlId(item.id) })
			.then((newControlId) => {
				console.log('cloned to control', newControlId)
			})
			.catch((e) => {
				console.error('Failed to clone', e)
			})
	}, [cloneMutation, item.id])

	const fullname = item.variableName ? `$(expression:${item.variableName})` : null

	return (
		<div onClick={doEdit} className="flex flex-row items-center gap-2 cursor-pointer">
			<div className="flex flex-col grow">
				{fullname ? (
					<span className="variable-style">
						{fullname}
						<CopyButton size="sm" title="Copy variable name" color="primary" variant="ghost" text={fullname} />
					</span>
				) : (
					<b>Unnamed</b>
				)}

				<span>{item.description ?? ''}</span>
			</div>

			<div className="action-buttons w-auto">
				<ButtonGroup>
					<Button onClick={doClone} title="Clone">
						<FontAwesomeIcon icon={faClone} />
					</Button>
					<Button onClick={doDelete} title="Delete">
						<FontAwesomeIcon icon={faTrash} />
					</Button>
				</ButtonGroup>
			</div>
		</div>
	)
})

function CreateCollectionButton() {
	const createMutation = useMutationExt(trpc.controls.expressionVariables.collections.add.mutationOptions())

	const doCreateCollection = useCallback(() => {
		createMutation.mutateAsync({ collectionName: 'New Collection' }).catch((e) => {
			console.error('Failed to add collection', e)
		})
	}, [createMutation])

	return (
		<Button color="info" size="sm" onClick={doCreateCollection}>
			<FontAwesomeIcon icon={faLayerGroup} /> Create Collection
		</Button>
	)
}

interface ExpressionVariableEditPanelHeadingProps {
	doCloseVariable: () => void
}

function ExpressionVariableEditPanelHeading({ doCloseVariable }: ExpressionVariableEditPanelHeadingProps) {
	return (
		<div className="secondary-panel-simple-header">
			<h4 className="panel-title">Edit Expression variable</h4>
			<div className="header-buttons">
				<ContextHelpButton action="/user-guide/config/variables#expression-variables" />
				<CloseButton closeFn={doCloseVariable} />
			</div>
		</div>
	)
}
